/**
 * ContentForge — Projects View
 *
 * Project cards with keyword counts, progress bars, and keyword assignment.
 */

import { html, nothing } from "lit";
import type { CfKeyword } from "../controllers/contentforge.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfProject = {
  id: string;
  name: string;
  siteId: string;
  description: string;
  keywordIds: string[];
  createdAt: number;
  updatedAt: number;
};

export type CfProjectsProps = {
  projects: CfProject[];
  keywords: CfKeyword[];
  loading: boolean;
  error: string | null;
  selectedProjectId: string | null;
  onRefresh: () => void;
  onCreate: (name: string, description: string) => void;
  onAssignKeyword: (projectId: string, keywordId: string) => void;
  onSelectProject: (projectId: string | null) => void;
};

// ─── Helpers ────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function projectProgress(project: CfProject, keywords: CfKeyword[]): number {
  if (project.keywordIds.length === 0) {
    return 0;
  }
  const projectKeywords = keywords.filter((k) => project.keywordIds.includes(k.id));
  if (projectKeywords.length === 0) {
    return 0;
  }
  const published = projectKeywords.filter((k) => k.stage === "published").length;
  return Math.round((published / projectKeywords.length) * 100);
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfProjects(props: CfProjectsProps) {
  const {
    projects,
    keywords,
    loading,
    error,
    selectedProjectId,
    onRefresh,
    onCreate,
    onAssignKeyword,
    onSelectProject,
  } = props;

  const selectedProject = selectedProjectId
    ? (projects.find((p) => p.id === selectedProjectId) ?? null)
    : null;

  return html`
    <div class="cf-projects">
      <!-- Header -->
      <div class="cf-tasks__header">
        <div class="cf-tasks__title">
          <h2>Projects</h2>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onRefresh}>
            Refresh
          </button>
        </div>
        ${error ? html`<div class="cf-error">${error}</div>` : nothing}
      </div>

      <!-- Stats -->
      <div class="cf-stats">
        <div class="cf-stats__item">
          <span class="cf-stats__value">${projects.length}</span>
          <span class="cf-stats__label">Total</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${projects.reduce((sum, p) => sum + p.keywordIds.length, 0)}</span>
          <span class="cf-stats__label">Keywords</span>
        </div>
      </div>

      <!-- New Project Form -->
      ${renderNewProjectForm(onCreate)}

      <!-- Loading -->
      ${
        loading
          ? html`
              <div class="cf-loading">Loading projects…</div>
            `
          : nothing
      }

      <!-- Project Grid + Detail -->
      <div class="cf-projects__layout">
        <div class="cf-projects__grid">
          ${
            projects.length === 0
              ? html`
                  <div class="cf-approvals__empty">
                    <div class="cf-approvals__empty-icon">📁</div>
                    <div class="cf-approvals__empty-title">No projects yet</div>
                    <div class="cf-approvals__empty-desc">Create a project to group keywords together.</div>
                  </div>
                `
              : projects.map((p) =>
                  renderProjectCard(p, keywords, selectedProjectId, onSelectProject),
                )
          }
        </div>

        ${selectedProject ? renderProjectDetail(selectedProject, keywords, onAssignKeyword) : nothing}
      </div>
    </div>
  `;
}

// ─── New Project Form ───────────────────────────────────────────────────

function renderNewProjectForm(onCreate: (name: string, description: string) => void) {
  return html`
    <div class="cf-new-task">
      <input
        type="text"
        class="cf-input"
        id="cf-new-project-name"
        placeholder="Project name…"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            const input = e.target as HTMLInputElement;
            const desc = document.getElementById("cf-new-project-desc") as HTMLInputElement;
            const name = input.value.trim();
            if (!name) {
              return;
            }
            onCreate(name, desc?.value.trim() ?? "");
            input.value = "";
            if (desc) {
              desc.value = "";
            }
          }
        }}
      />
      <input
        type="text"
        class="cf-input"
        id="cf-new-project-desc"
        placeholder="Description (optional)…"
      />
      <button
        class="cf-btn cf-btn--primary"
        @click=${() => {
          const nameInput = document.getElementById("cf-new-project-name") as HTMLInputElement;
          const descInput = document.getElementById("cf-new-project-desc") as HTMLInputElement;
          const name = nameInput?.value.trim();
          if (!name) {
            return;
          }
          onCreate(name, descInput?.value.trim() ?? "");
          nameInput.value = "";
          if (descInput) {
            descInput.value = "";
          }
        }}
      >
        + New Project
      </button>
    </div>
  `;
}

// ─── Project Card ───────────────────────────────────────────────────────

function renderProjectCard(
  project: CfProject,
  keywords: CfKeyword[],
  selectedId: string | null,
  onSelect: (id: string | null) => void,
) {
  const progress = projectProgress(project, keywords);
  const isSelected = selectedId === project.id;
  return html`
    <div
      class="cf-project-card ${isSelected ? "cf-project-card--selected" : ""}"
      @click=${() => onSelect(isSelected ? null : project.id)}
    >
      <div class="cf-project-card__name">${project.name}</div>
      ${project.description ? html`<div class="cf-project-card__desc">${project.description}</div>` : nothing}
      <div class="cf-project-card__meta">
        <span>${project.keywordIds.length} keyword${project.keywordIds.length !== 1 ? "s" : ""}</span>
        <span>${formatRelativeTime(project.updatedAt)}</span>
      </div>
      <div class="cf-project-card__progress">
        <div class="cf-project-card__progress-bar" style="width: ${progress}%"></div>
      </div>
      <div class="cf-project-card__progress-label">${progress}% complete</div>
    </div>
  `;
}

// ─── Project Detail Panel ───────────────────────────────────────────────

function renderProjectDetail(
  project: CfProject,
  keywords: CfKeyword[],
  onAssign: (projectId: string, keywordId: string) => void,
) {
  const projectKeywords = keywords.filter((k) => project.keywordIds.includes(k.id));
  const unassigned = keywords
    .filter((k) => !k.projectId || k.projectId === project.id)
    .filter((k) => !project.keywordIds.includes(k.id));

  return html`
    <div class="cf-project-detail">
      <h3>${project.name}</h3>
      ${project.description ? html`<p class="cf-project-detail__desc">${project.description}</p>` : nothing}

      <!-- Assigned Keywords -->
      <div class="cf-project-detail__section">
        <h4>Assigned Keywords (${projectKeywords.length})</h4>
        ${
          projectKeywords.length === 0
            ? html`
                <p class="cf-project-detail__empty">No keywords assigned yet.</p>
              `
            : html`
                <div class="cf-project-detail__keywords">
                  ${projectKeywords.map(
                    (kw) => html`
                      <div class="cf-project-detail__keyword">
                        <span class="cf-project-detail__keyword-text">${kw.text}</span>
                        <span class="cf-project-detail__keyword-stage">${kw.stage}</span>
                      </div>
                    `,
                  )}
                </div>
              `
        }
      </div>

      <!-- Assign New Keyword -->
      ${
        unassigned.length > 0
          ? html`
              <div class="cf-project-detail__section">
                <h4>Add Keyword</h4>
                <div class="cf-new-task">
                  <select class="cf-select" id="cf-assign-keyword-select">
                    ${unassigned.map((k) => html`<option value=${k.id}>${k.text}</option>`)}
                  </select>
                  <button
                    class="cf-btn cf-btn--primary cf-btn--sm"
                    @click=${() => {
                      const sel = document.getElementById(
                        "cf-assign-keyword-select",
                      ) as HTMLSelectElement;
                      if (sel?.value) {
                        onAssign(project.id, sel.value);
                      }
                    }}
                  >
                    Assign
                  </button>
                </div>
              </div>
            `
          : nothing
      }
    </div>
  `;
}
